import Country from 'country-state-city/lib/country';
import React, { useMemo, useState } from 'react';
import type { TextStyle, ViewStyle } from 'react-native';

import { STRINGS } from '@/constants/strings';
import type { TypographyToken } from '@/theme/typography';

import { FieldTrigger } from './field-trigger';
import { SearchableSelectSheet, type SearchableSelectItem } from './searchable-select-sheet';

const strings = STRINGS.common.countryRegionPicker;

export type CountryRegionFieldProps = {
  label?: string;
  required?: boolean;
  placeholder?: string;
  /** Selected country ISO code (e.g. "US"), or null/'' when unset. */
  value?: string | null;
  onValueChange: (isoCode: string) => void;
  disabled?: boolean;
  status?: 'default' | 'error';
  errorMessage?: string;
  /** Show the +dial code on each row + the trigger (supported by the country data). */
  showDialCode?: boolean;
  // Styling passthrough so the field lines up with the surrounding form fields.
  uppercaseLabel?: boolean;
  labelVariant?: TypographyToken;
  labelGap?: number;
  labelStyle?: TextStyle | TextStyle[];
  triggerVariant?: TypographyToken;
  triggerStyle?: ViewStyle | ViewStyle[];
  disableStyle?: ViewStyle | ViewStyle[];
};

/**
 * Address Country/Region field: a form-styled trigger that opens a searchable, alphabetically
 * grouped country picker (with United States featured under "#" and each row showing its
 * +dial code). Holds/returns the ISO code so it can drive the dependent State/Province options.
 */
export function CountryRegionField({
  label,
  required = false,
  placeholder = strings.placeholder,
  value,
  onValueChange,
  disabled = false,
  status = 'default',
  errorMessage,
  uppercaseLabel = false,
  labelVariant,
  labelGap,
  labelStyle,
  triggerVariant,
  triggerStyle,
  disableStyle,
}: CountryRegionFieldProps) {
  const [open, setOpen] = useState(false);

  const items = useMemo<SearchableSelectItem[]>(
    () =>
      Country.getAllCountries()
        .map(country => ({
          value: country.isoCode,
          label: country.name,
          meta: country.phonecode.startsWith('+') ? country.phonecode : `+${country.phonecode}`,
        }))
        .sort((a, b) => {
          if (a.value === 'US') return -1;
          if (b.value === 'US') return 1;
          return a.label.localeCompare(b.label);
        }),
    [],
  );

  const displayValue = useMemo(
    () => (value ? (items.find(item => item.value === value)?.label ?? null) : null),
    [items, value],
  );

  return (
    <>
      <FieldTrigger
        label={label}
        required={required}
        uppercaseLabel={uppercaseLabel}
        labelVariant={labelVariant}
        labelGap={labelGap}
        labelStyle={labelStyle}
        placeholder={placeholder}
        displayValue={displayValue}
        disabled={disabled}
        status={status}
        errorMessage={errorMessage}
        triggerVariant={triggerVariant}
        triggerStyle={triggerStyle}
        disableStyle={disableStyle}
        onPress={() => setOpen(true)}
      />
      <SearchableSelectSheet
        open={open}
        onOpenChange={setOpen}
        title={strings.title}
        searchPlaceholder={strings.searchPlaceholder}
        items={items}
        selectedValue={value}
        onSelect={onValueChange}
        grouped
        featuredValues={['US']}
        featuredSectionTitle="#"
        emptyText={strings.noResults}
      />
    </>
  );
}

export default CountryRegionField;
